import { useEffect, useMemo, useState } from 'react';

import {
  useDeletePayrunAmendment,
  useGetAmendmentByIdQueryV2,
} from '@xemplo/amendment-query';
import { ButtonType } from '@xemplo/button';
import { HeaderIconMode } from '@xemplo/header-icon';
import { DawnTriangleAlert } from '@xemplo/icons';
import { ModalBodyOverflow, ModalSize, ModalTheme } from '@xemplo/modal';
import { PromptProps, PromptType } from '@xemplo/prompts';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import * as S from '../payrun-amendment-detail.styles';
import { PayrunAmendmentDetailFooterProps } from '../payrun-amendment-detail.types';

export const useConfirmDeletePrompt = (props: PayrunAmendmentDetailFooterProps) => {
  const { amendmentId, onSuccess } = props;
  const [promptVisible, setPromptVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addToast } = useToast();
  const { data: amendment } = useGetAmendmentByIdQueryV2({ id: amendmentId });

  const { mutateAsync: deleteAmendment } = useDeletePayrunAmendment({
    amendmentId,
    payrunId: amendment?.payrunId ?? '',
  });

  //config for prompt modal
  const initalPromptProps = useMemo<PromptProps>(() => {
    return {
      type: PromptType.Alert,

      theme: ModalTheme.Light,
      size: ModalSize.Large,
      closeButton: { hide: true },
      header: {
        title: 'Remove Amendment',
        headerIcon: {
          icon: <DawnTriangleAlert />,
          mode: HeaderIconMode.ActionItem,
        },
      },
      body: {
        content: (
          <S.PromptBody>
            Are you sure you want to remove amendment? You can't undo changes.
          </S.PromptBody>
        ),
        overflow: ModalBodyOverflow.Extended,
      },
      footer: {
        btnPrimary: {
          label: 'Remove',
          ariaLabel: 'confirm remove button',
          onClickAsync: async () => {
            const [, error] = await safeCallAsync(deleteAmendment());
            if (error) {
              setErrorMessage(
                (error as Error).message ?? 'Something went wrong, please try again.',
              );
              return;
            }
            addToast({ text: 'Amendment deleted', type: ToastType.Confirmation });
            setPromptVisible(false);
            onSuccess?.();
          },
          type: ButtonType.Primary,
        },
        btnSecondary: {
          label: 'Cancel',
          ariaLabel: 'Cancel remove button',
          type: ButtonType.Secondary,
          onClick: () => {
            setErrorMessage(null);
            setPromptVisible(false);
          },
        },
      },
    };
  }, [deleteAmendment]);

  useEffect(() => {
    if (errorMessage) {
      setPromptProps({
        ...initalPromptProps,
        body: {
          content: (
            <>
              <S.PromptBody>
                Are you sure you want to remove amendment? You can't undo changes.
              </S.PromptBody>
              <S.ErrorMessage>
                <DawnTriangleAlert />
                {errorMessage}
              </S.ErrorMessage>
            </>
          ),
          overflow: ModalBodyOverflow.Extended,
        },
      });
    } else {
      setPromptProps(initalPromptProps);
    }
  }, [errorMessage, initalPromptProps]);

  const [promptProps, setPromptProps] = useState<PromptProps>(initalPromptProps);

  return { promptProps, promptVisible, setPromptVisible };
};
